import React, { useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { isEmpty } from 'lodash-es';
import { Cascader } from 'tea-component';

import type { CascaderOption } from 'tea-component';

import { PERMISSION_ALL_VALUE } from '../../constants';

import './index.scss';

interface IPermissionSelectProps {
  options: CascaderOption[];
  value: string[][];
  onChange?: (value: string[][]) => void;
  onBlur?: () => void;
}

export const findLabelById = (data: any[], id: string) => {
  for (let i = 0; i < data?.length; i++) {
    if (data[i]?.id === id) {
      return data[i]?.label; // 如果当前层级找到了对应的 id，则返回对应的 label
    }
    if (data[i]?.children) {
      const result: any = findLabelById(data[i]?.children, id); // 递归查找子级
      if (result) {
        return result; // 如果子级中找到了对应的 id，则返回对应的 label
      }
    }
  }
  return null; // 如果未找到对应的 id，则返回 null
};

const normalizeCascaderValue = (value: unknown): string[][] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string[] => Array.isArray(item));
};

const getAllCascaderValue = (options: CascaderOption[]): string[][] =>
  options
    .map((option) => {
      const parentValue = String(option?.value ?? '');
      return parentValue ? [parentValue] : [];
    })
    .filter((item) => item.length > 0);

const getLeafKeySet = (paths: string[][], options: CascaderOption[]): Set<string> => {
  const keys = new Set<string>();

  paths.forEach(([parentValue, childValue]) => {
    if (childValue) {
      keys.add(`${parentValue}::${childValue}`);
      return;
    }

    const option = options.find((item) => item?.value === parentValue);
    // @ts-ignore
    const children = option?.children as CascaderOption[] | undefined;
    if (isEmpty(children)) {
      keys.add(parentValue);
      return;
    }

    children!.forEach((child) => {
      const leafValue = String(child?.value ?? '');
      if (leafValue) keys.add(`${parentValue}::${leafValue}`);
    });
  });

  return keys;
};

const isSameLeafSelection = (
  left: string[][],
  right: string[][],
  options: CascaderOption[],
): boolean => {
  const leftKeys = getLeafKeySet(left, options);
  const rightKeys = getLeafKeySet(right, options);

  return leftKeys.size === rightKeys.size && [...leftKeys].every((key) => rightKeys.has(key));
};

const isPermissionAllSelected = (value: string[][]): boolean => {
  return value.length === 1 && value[0]?.length === 1 && value[0][0] === PERMISSION_ALL_VALUE;
};

const PermissionSelect: React.FC<IPermissionSelectProps> = (props) => {
  const { options, value, onChange, onBlur } = props;

  const { formatMessage } = useIntl();
  const allLabel = formatMessage({ id: 'dbu_report_permission_all' });
  const [panelOpen, setPanelOpen] = useState(false);

  const safeValue = normalizeCascaderValue(value);
  const allCascaderValue = useMemo(() => getAllCascaderValue(options || []), [options]);
  const isAllSelected = isPermissionAllSelected(safeValue);
  const cascaderValue = isAllSelected ? allCascaderValue : safeValue;

  return (
    <Cascader
      key={isAllSelected ? 'permission-all' : 'permission-partial'}
      className={isAllSelected ? 'dbu-permission-select--all-selected' : undefined}
      style={{ minWidth: 300 }}
      overlayClassName="dbu-report-cascader-overlay"
      multiple
      clearable
      searchable
      type="menu"
      placement="bottom-start"
      valueMode="parentFirst"
      showMode="parentFirst"
      open={panelOpen}
      onOpenChange={setPanelOpen}
      placeholder={formatMessage({ id: 'dbu_report_permission_placeholder' })}
      data={options || []}
      value={cascaderValue}
      onChange={(nextValue) => {
        const next = normalizeCascaderValue(nextValue);

        // 清空：取消全部权限勾选
        if (isEmpty(next)) {
          onChange?.([]);
          return;
        }

        if (isAllSelected) {
          // 仍为全选，忽略无意义回写
          if (isSameLeafSelection(next, allCascaderValue, options || [])) {
            return;
          }
          // 面板关闭时的取消（清除按钮 / 「全部」标签关闭）→ 清空全部勾选
          if (!panelOpen) {
            onChange?.([]);
            return;
          }
          // 面板打开时在下拉中取消部分勾选 → 保留剩余选择
          onChange?.(next);
          return;
        }

        // 显式勾选到全部时，收敛为「全部」哨兵值
        if (
          !isEmpty(allCascaderValue) &&
          isSameLeafSelection(next, allCascaderValue, options || [])
        ) {
          onChange?.([[PERMISSION_ALL_VALUE]]);
          return;
        }

        onChange?.(next);
      }}
      onBlur={onBlur}
      valueRender={(optionsPayload) => {
        if (isAllSelected) {
          return allLabel;
        }

        if (isEmpty(optionsPayload)) {
          return undefined;
        }

        if (!isEmpty(options)) {
          const leaf = optionsPayload?.[optionsPayload?.length - 1];
          // @ts-ignore
          const label = findLabelById(options, +leaf?.value);
          if (label) {
            return `${optionsPayload?.[0]?.label}: ${label || leaf?.label}`;
          }
          return label || leaf?.label;
        }
      }}
    />
  );
};

export default PermissionSelect;
